import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

type ModalType =
  | 'confirm-delete'
  | 'upgrade-subscription'
  | 'generation-preview'
  | 'settings'
  | null;

interface ModalData {
  title?: string;
  message?: string;
  onConfirm?: () => void;
  generationId?: string;
  [key: string]: unknown;
}

interface UIState {
  // Theme
  theme: 'light' | 'dark' | 'system';

  // Sidebar
  sidebarOpen: boolean;
  sidebarCollapsed: boolean;

  // Modals
  activeModal: ModalType;
  modalData: ModalData | null;

  // Toast notifications (for quick feedback)
  toasts: Toast[];

  // Actions - Theme
  setTheme: (theme: 'light' | 'dark' | 'system') => void;

  // Actions - Sidebar
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  toggleSidebarCollapse: () => void;

  // Actions - Modals
  openModal: (type: ModalType, data?: ModalData) => void;
  closeModal: () => void;

  // Actions - Toasts
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
}

interface Toast {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  title: string;
  message?: string;
  duration?: number;
}

export const useUIStore = create<UIState>()(
  persist(
    (set, get) => ({
      // Initial state
      theme: 'dark',
      sidebarOpen: false,
      sidebarCollapsed: false,
      activeModal: null,
      modalData: null,
      toasts: [],

      // Theme
      setTheme: (theme) => {
        set({ theme });

        // Apply theme to document
        const root = document.documentElement;
        root.classList.remove('light', 'dark');

        if (theme === 'system') {
          const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
          root.classList.add(prefersDark ? 'dark' : 'light');
        } else {
          root.classList.add(theme);
        }
      },

      // Sidebar
      toggleSidebar: () =>
        set({ sidebarOpen: !get().sidebarOpen }),

      setSidebarOpen: (open) =>
        set({ sidebarOpen: open }),

      toggleSidebarCollapse: () =>
        set({ sidebarCollapsed: !get().sidebarCollapsed }),

      // Modals
      openModal: (type, data = undefined) =>
        set({ activeModal: type, modalData: data ?? null }),

      closeModal: () =>
        set({ activeModal: null, modalData: null }),

      // Toasts
      addToast: (toast) => {
        const id = `toast_${Date.now()}`;
        const newToast: Toast = { ...toast, id };

        set({ toasts: [...get().toasts, newToast] });

        // Auto-remove after duration
        const duration = toast.duration ?? 5000;
        if (duration > 0) {
          setTimeout(() => {
            get().removeToast(id);
          }, duration);
        }
      },

      removeToast: (id) =>
        set({ toasts: get().toasts.filter((t) => t.id !== id) }),
    }),
    {
      name: 'soundbyte-ui',
      partialize: (state) => ({
        theme: state.theme,
        sidebarCollapsed: state.sidebarCollapsed,
      }),
    }
  )
);

// Selector hooks - using useShallow to prevent infinite loops
export const useTheme = () => useUIStore((state) => state.theme);
export const useSidebar = () =>
  useUIStore(
    useShallow((state) => ({
      isOpen: state.sidebarOpen,
      isCollapsed: state.sidebarCollapsed,
    }))
  );
export const useModal = () =>
  useUIStore(
    useShallow((state) => ({
      type: state.activeModal,
      data: state.modalData,
      isOpen: state.activeModal !== null,
    }))
  );
export const useToasts = () => useUIStore((state) => state.toasts);
